import { ChangeDetectionStrategy, Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { EmailPreferencesApiService } from './email-preferences-api.service';

type PageState = 'loading' | 'loaded' | 'success' | 'error';

@Component({
  standalone: true,
  selector: 'app-email-preferences',
  templateUrl: './email-preferences.component.html',
  styleUrls: ['./email-preferences.component.scss'],
  imports: [CommonModule, RouterLink, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailPreferencesComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly emailPreferencesApi = inject(EmailPreferencesApiService);

  email = signal<string>('');
  billingEmailsEnabled = signal<boolean>(true);
  pageState = signal<PageState>('loading');
  errorMessage = signal<string>('');
  successMessage = signal<string>('');
  isUpdating = signal<boolean>(false);

  emailMasked = computed(() => {
    const emailValue = this.email();
    if (!emailValue) return '';
    const [local, domain] = emailValue.split('@');
    if (!domain) return emailValue;
    const maskedLocal =
      local.length > 2 ? `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}` : local;
    return `${maskedLocal}@${domain}`;
  });

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const emailParam = params['email'];
      if (emailParam) {
        this.email.set(emailParam.toLowerCase());
        this.loadPreferences();
      } else {
        this.pageState.set('loaded');
      }
    });
  }

  loadPreferences(): void {
    const emailValue = this.email();
    if (!emailValue) {
      this.pageState.set('loaded');
      return;
    }

    this.pageState.set('loading');
    this.emailPreferencesApi.getStatus(emailValue).subscribe({
      next: (response) => {
        this.billingEmailsEnabled.set(response.billingEmailsEnabled);
        this.pageState.set('loaded');
      },
      error: (error) => {
        console.error('Failed to load email preferences:', error);
        this.errorMessage.set('Unable to load your preferences. Please try again.');
        this.pageState.set('error');
      },
    });
  }

  onEmailSubmit(): void {
    const emailValue = this.email().trim().toLowerCase();
    if (!emailValue || !this.isValidEmail(emailValue)) {
      this.errorMessage.set('Please enter a valid email address.');
      return;
    }
    this.email.set(emailValue);
    this.errorMessage.set('');
    this.loadPreferences();
  }

  toggleBillingEmails(): void {
    const emailValue = this.email();
    if (!emailValue) return;

    this.isUpdating.set(true);
    const newState = !this.billingEmailsEnabled();

    const request = newState
      ? this.emailPreferencesApi.subscribe(emailValue)
      : this.emailPreferencesApi.unsubscribe(emailValue);

    request.subscribe({
      next: (response) => {
        if (response.success) {
          this.billingEmailsEnabled.set(newState);
          this.successMessage.set(response.message);
          this.pageState.set('success');
        } else {
          this.errorMessage.set(response.message);
        }
        this.isUpdating.set(false);
      },
      error: (error) => {
        console.error('Failed to update email preferences:', error);
        this.errorMessage.set('Something went wrong. Please try again.');
        this.isUpdating.set(false);
      },
    });
  }

  resetToLoaded(): void {
    this.pageState.set('loaded');
    this.successMessage.set('');
    this.errorMessage.set('');
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

